import { useEffect, useMemo, useState, useCallback } from "react";
/* InviteButton injected below */
function InviteButton({ projectId, contractor }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailTo, setEmailTo] = useState(null);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/contractors/${contractor.id}/invites`);
      setUrl(data.invite_url);
      setEmailSent(!!data.email_sent);
      setEmailTo(data.email_to || data.email || null);
      if (data.email_sent) {
        toast.success(`Activation email sent to ${data.email_to || data.email}`);
      } else if (data.email) {
        toast.info("Couldn't send the email — share the link below instead", { duration: 5000 });
      } else {
        toast.success("Invite link created — share it with the contractor");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (url) {
    return (
      <div className="space-y-2">
        {emailSent ? (
          <div
            className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-medium px-3 py-2"
            data-testid={`invite-email-sent-${contractor.id}`}
          >
            ✓ Activation email sent to <span className="font-semibold">{emailTo}</span>
          </div>
        ) : (
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
            Share this link with {contractor.name}
          </div>
        )}
        <div className="flex gap-1 items-center">
          <input
            value={url} readOnly
            className="flex-1 text-[11px] font-mono px-2 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg"
            onFocus={(e) => e.target.select()}
            data-testid={`invite-link-${contractor.id}`}
          />
          <button
            onClick={copy}
            className="p-1.5 border border-zinc-200 rounded-lg hover:bg-zinc-50"
            data-testid={`copy-invite-${contractor.id}`}
            title="Copy"
          >
            <Copy size={14} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={create}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#007BFF] hover:underline disabled:opacity-50"
      data-testid={`create-invite-${contractor.id}`}
    >
      <LinkIcon size={12} weight="bold" /> {busy ? "Sending..." : (contractor.contact_email ? "Send activation email" : "Generate invite link")}
    </button>
  );
}

function ShareSheetButton({ projectId, contractor }) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/contractors/${contractor.id}/share-links`);
      setLinks(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async (v) => {
    setOpen(v);
    if (v) await load();
  };

  const createLink = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/contractors/${contractor.id}/share-links`);
      toast.success("Share link created");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (shareId) => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/contractors/${contractor.id}/share-links/${shareId}/revoke`);
      toast.success("Link revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const active = links.filter((l) => !l.revoked);

  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#001E44] hover:text-[#007BFF] transition-colors"
          data-testid={`share-sheet-${contractor.id}`}
        >
          <ShareNetwork size={12} weight="bold" /> Share procurement sheet
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="share-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
            Share {contractor.name}&apos;s procurement sheet
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">
          Anyone with the link can view this contractor&apos;s items on this project — read-only, no login required. Revoke at any time.
        </p>

        <div className="space-y-2 pt-2">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : active.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">No active share links yet.</div>
          ) : (
            active.map((l) => (
              <div key={l.id} className="ctrl-card p-3 space-y-2" data-testid={`share-link-${l.id}`}>
                <div className="flex items-center gap-2">
                  <input
                    value={l.share_url}
                    readOnly
                    className="flex-1 text-[11px] font-mono px-2 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg"
                    onFocus={(e) => e.target.select()}
                    data-testid={`share-url-${l.id}`}
                  />
                  <button
                    onClick={() => copy(l.share_url)}
                    className="p-1.5 border border-zinc-200 rounded-lg hover:bg-zinc-50"
                    title="Copy"
                    data-testid={`copy-share-${l.id}`}
                  >
                    <Copy size={14} weight="bold" />
                  </button>
                  <button
                    onClick={() => revoke(l.id)}
                    disabled={busy}
                    className="p-1.5 border border-red-100 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    title="Revoke"
                    data-testid={`revoke-share-${l.id}`}
                  >
                    <XCircle size={14} weight="bold" />
                  </button>
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                  Created {new Date(l.created_at).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            onClick={createLink}
            disabled={busy}
            className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-sm font-semibold px-5"
            data-testid="create-share-link"
          >
            <ShareNetwork size={14} weight="bold" className="mr-2" />
            {busy ? "Working…" : "Create new share link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { StatusBar, PriorityChip } from "../components/StatusBar";
import { DatePicker } from "../components/DatePicker";
import { logDev } from "../lib/log";
import { STATUSES, PRIORITIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Plus, MapPin, ArrowLeft, Trash, UserPlus, Package, CaretRight,
  Link as LinkIcon, Copy, ListBullets, Check, ShareNetwork, XCircle, Crown,
  EnvelopeSimple, FileCsv, FilePdf, ClockCounterClockwise,
  MagnifyingGlass, CaretUp, CaretDown, CaretUpDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ItemDetailSheet, { OverdueBadge } from "../components/ItemDetailSheet";
import ImportScheduleDialog from "../components/ImportScheduleDialog";
import CriticalPathBanner from "../components/CriticalPathBanner";
import ProjectActivityPanel from "../components/ProjectActivityPanel";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [digest, setDigest] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [newItemOpen, setNewItemOpen] = useState(false);

  const isOwner = !!project && project.owner_id === user?.id;
  const canManageContractors = user?.role !== "contractor" || isOwner;  // admin + PM, plus standalone contractor in their own project
  const canManageItems = user?.role === "admin" || user?.role === "contractor" || isOwner;
  // A project is "contractor-run" when a contractor owns it. Every project
  // member (owner OR teammate) should see the team-members section for such
  // projects; only the owner can add/remove people.
  const isContractorRunProject = project?.owner_role === "contractor";
  const isStandaloneContractor = user?.role === "contractor" && isOwner;
  const memberLabel = isContractorRunProject ? "team member" : "contractor";
  const memberLabelPlural = isContractorRunProject ? "team members" : "contractor accounts";
  // Drill-in flow: on a PM-run project, project managers (and admins) should
  // NOT see a flat "all items" list — they must first pick a contractor
  // company. Contractors viewing a PM-run project see their own items
  // directly (backend already scopes). Contractor-run projects keep the
  // flat list.
  const useContractorDrillIn = !isContractorRunProject
    && (user?.role === "project_manager" || user?.role === "admin");
  // The auto-created default bucket for standalone-contractor projects
  // (the one with is_default === true and user_id matching the project owner).
  const defaultBucket = contractors.find((c) => c.is_default && c.user_id === project?.owner_id);

  const load = useCallback(async () => {
    // NOTE: don't flip `loading` to true on re-fetch — it would unmount
    // <ItemDetailSheet /> mid-edit (e.g. when adding a custom field), which
    // presents to the user as a page refresh. The initial useState(true)
    // already covers the first-mount skeleton.
    try {
      const [p, c, i, d] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/contractors`),
        api.get(`/projects/${id}/items`),
        api.get(`/projects/critical-path`, { params: { project_id: id } }).catch(() => ({ data: null })),
      ]);
      setProject(p.data);
      setContractors(c.data);
      setItems(i.data);
      setDigest(d.data);
      // Every contractor viewer (owner or teammate) needs their team roster
      // so we can render the "Your team" section on the project. Non-owners
      // still see the list but can't add/remove.
      if (user?.role === "contractor") {
        try {
          const t = await api.get("/team/contractor-team");
          setTeamMembers(t.data);
          if (canManageContractors) {
            setUsers(
              t.data
                .filter((m) => m.id !== user.id)
                .map((m) => ({ id: m.id, name: m.name, email: m.email, role: "contractor" }))
            );
          }
        } catch (e) {
          // Non-fatal — teammate list is optional; log so a real failure
          // isn't invisible in dev.
          logDev("project.contractor-team", e);
        }
      } else if (canManageContractors) {
        try {
          const u = await api.get("/auth/users");
          setUsers(u.data.filter((x) => x.role === "contractor"));
        } catch (e) {
          logDev("project.users-fetch", e);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, canManageContractors, user?.role, user?.id]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    // Drill-in flow: PM/admin on a PM-run project must explicitly pick a
    // contractor before seeing any items. selectedContractor === null =>
    // nothing shown (they haven't drilled in yet).
    if (selectedContractor === null) {
      return useContractorDrillIn ? [] : items;
    }
    if (selectedContractor === "all") return items;
    return items.filter((i) => i.contractor_account_id === selectedContractor);
  }, [items, selectedContractor, useContractorDrillIn]);

  const selectedContractorObj = useMemo(
    () => contractors.find((c) => c.id === selectedContractor) || null,
    [contractors, selectedContractor],
  );

  // Item table filter + sort. Search matches Code, Name, Supplier, and
  // PO# — the four fields a PM most often knows off the top of their head
  // when they need to find an item fast. Sort defaults to Code ascending
  // when populated (Code is the natural row identifier), otherwise the
  // original order (created_at desc from the API) is preserved.
  const [itemSearch, setItemSearch] = useState("");
  const [itemSort, setItemSort] = useState({ key: null, dir: "asc" });

  const toggleSort = (key) => {
    setItemSort((prev) =>
      prev.key !== key
        ? { key, dir: "asc" }
        : prev.dir === "asc"
          ? { key, dir: "desc" }
          : { key: null, dir: "asc" }  // third click clears
    );
  };

  const visibleItems = useMemo(() => {
    let list = filteredItems;
    const q = itemSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        (i.code || "").toLowerCase().includes(q)
        || (i.name || "").toLowerCase().includes(q)
        || (i.supplier || "").toLowerCase().includes(q)
        || (i.purchase_order || "").toLowerCase().includes(q)
      );
    }
    if (itemSort.key) {
      const { key, dir } = itemSort;
      const mul = dir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const av = (a[key] ?? "").toString().toLowerCase();
        const bv = (b[key] ?? "").toString().toLowerCase();
        // Empty strings always sort to the end, regardless of direction —
        // means blank codes never crowd out the codes you're scanning for.
        if (!av && bv) return 1;
        if (av && !bv) return -1;
        return av.localeCompare(bv, undefined, { numeric: true }) * mul;
      });
    }
    return list;
  }, [filteredItems, itemSearch, itemSort]);

  if (loading || !project) {
    return (
      <AppShell>
        <div className="h-64 bg-zinc-100 rounded-md animate-pulse" />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-10" data-testid="project-detail-page">
        <div>
          <Link to="/projects" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-[#001E44] transition-colors mb-4" data-testid="back-to-projects">
            <ArrowLeft size={12} weight="bold" /> All projects
          </Link>
          <div className="ctrl-card overflow-hidden">
            <div className="relative h-52 lg:h-64 bg-gradient-to-br from-[#001E44] via-[#012a5c] to-[#0a3b7c]">
              {project.thumbnail_url && (
                <img src={project.thumbnail_url} alt={project.name} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#001E44] via-[#001E44]/40 to-transparent" />
              <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                <button
                  onClick={() => setActivityOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 hover:bg-white text-[#001E44] text-xs sm:text-sm font-semibold shadow-md backdrop-blur-sm transition-all"
                  data-testid="project-activity-button"
                  title="Project activity log"
                >
                  <ClockCounterClockwise size={14} weight="bold" /> Activity
                </button>
                <ProjectShareMenu projectId={id} projectName={project.name} items={items} contractors={contractors} canShare={canManageItems} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                <span className="label-eyebrow text-blue-200">Project</span>
                <h1 className="font-display text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight mt-1 !text-white">
                  {project.name}
                </h1>
                <div className="flex items-center gap-2 mt-3 text-zinc-100">
                  <MapPin size={14} weight="bold" />
                  <span className="text-sm">{project.location || "No location"}</span>
                </div>
              </div>
            </div>
            {project.description && (
              <div className="p-6 text-sm text-zinc-600 border-t border-zinc-100 leading-relaxed">
                {project.description}
              </div>
            )}
          </div>
        </div>

        {/* Critical Path — per-project digest, always visible */}
        {digest && (
          <CriticalPathBanner
            digest={digest}
            projectId={id}
            canEmailContractors={!(user?.role === "contractor" && isOwner)}
          />
        )}

        {/* Contractor accounts */}
        <section className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="label-eyebrow text-[#007BFF]">
                {isContractorRunProject ? "Your team" : "Trade partners"}
              </span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1 capitalize">
                {memberLabelPlural}
              </h2>
            </div>
            {(canManageContractors && (isContractorRunProject ? isOwner : true)) && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-sm font-semibold px-5 py-2.5 h-auto shadow-sm hover:shadow-md transition-all" data-testid="new-contractor-button">
                    <UserPlus size={14} weight="bold" className="mr-2" /> Add {memberLabel}
                  </Button>
                </DialogTrigger>
                {isContractorRunProject ? (
                  <AddProjectTeamMemberDialog projectId={id} onDone={load} />
                ) : (
                  <NewContractorDialog projectId={id} users={users} onCreated={load} memberLabel={memberLabel} isStandaloneContractor={isStandaloneContractor} />
                )}
              </Dialog>
            )}
          </div>

          {isContractorRunProject ? (
            // Show every teammate who has been explicitly added to THIS
            // project (per-project access model). The owner themselves is
            // always shown. Non-owner members see the same list read-only.
            (() => {
              const projectMemberIds = new Set([
                project.owner_id,
                ...(project.members || []),
              ]);
              const visibleTeam = teamMembers.filter(
                (m) => projectMemberIds.has(m.id)
                  && (m.team_access === "full" || m.team_access === "editor")
              );
              if (visibleTeam.length === 0) {
                return (
                  <div className="ctrl-card p-8 text-center" data-testid="empty-team-state">
                    <span className="label-eyebrow">
                      {isOwner ? "Just you on this project" : "No team yet"}
                    </span>
                    <p className="font-display text-lg font-bold tracking-tight mt-2 text-[#001E44]">
                      {isOwner
                        ? "Add teammates from your company to collaborate on this project."
                        : "The project owner hasn't added other teammates to this project yet."}
                    </p>
                    {isOwner && (
                      <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
                        Team members you add here can see and amend this project&apos;s procurement items.
                        Adding a teammate here does <span className="font-semibold">not</span> grant them access to your other projects.
                      </p>
                    )}
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-team-list">
                  {visibleTeam.map((m) => (
                    <div key={m.id} className="group ctrl-card p-5 hover:shadow-md transition-shadow relative" data-testid={`project-team-member-${m.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-xl flex items-center justify-center font-display font-semibold">
                          {m.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-base leading-tight text-[#001E44] truncate flex items-center gap-1.5">
                            {m.name}
                            {m.is_team_owner && (
                              <Crown size={11} weight="fill" className="text-[#007BFF] shrink-0" title="Team owner" />
                            )}
                            {m.id === user?.id && (
                              <span className="chip text-[9px] bg-zinc-100 text-zinc-500 ml-1">You</span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 truncate">
                            {m.job_title || (m.is_team_owner ? "Team owner" : "Teammate")}
                          </div>
                        </div>
                        {!m.is_team_owner && isOwner && (
                          <RemoveProjectMemberButton
                            projectId={id}
                            member={m}
                            onRemoved={load}
                          />
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
                        <span className={`chip text-[10px] ${
                          m.team_access === "full" ? "bg-[#007BFF]/10 text-[#007BFF]"
                          : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {m.team_access === "full" ? "Full access" : "Editor"}
                        </span>
                        <a
                          href={`mailto:${m.email}`}
                          className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-400 hover:text-[#007BFF] transition-colors truncate ml-2"
                          title={m.email}
                        >
                          {m.email}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : contractors.length === 0 ? (
            <div className="ctrl-card p-8 text-center">
              <span className="label-eyebrow">No {memberLabelPlural} yet</span>
              <p className="font-display text-lg font-bold tracking-tight mt-2">
                Add {memberLabelPlural} to start tracking their procurement items.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contractors.map((c) => {
                const cItems = items.filter((i) => i.contractor_account_id === c.id);
                // Server-computed urgency counters (see _annotate_contractor_urgency
                // in backend/routers/projects.py). Any overdue OR high-priority
                // ('urgent') item that isn't yet delivered lights the card red so
                // a PM scanning the contractor grid can pounce on trouble spots.
                const overdue = c.overdue_count || 0;
                const urgent = c.urgent_count || 0;
                const urgencyLevel = overdue > 0 ? "overdue" : urgent > 0 ? "urgent" : null;
                return (
                  <div
                    key={c.id}
                    className={`ctrl-card p-5 relative transition-all duration-200 ${
                      urgencyLevel === "overdue"
                        ? "glow-overdue"
                        : urgencyLevel === "urgent"
                          ? "border-amber-400 ring-1 ring-amber-300/60 shadow-[0_0_0_3px_rgba(245,158,11,0.10),0_0_18px_-4px_rgba(245,158,11,0.35)]"
                          : ""
                    }`}
                    data-testid={`contractor-card-${c.id}`}
                    data-urgency={urgencyLevel || "none"}
                  >
                    {urgencyLevel && (
                      <div
                        className={`absolute -top-2 -right-2 chip text-[10px] font-semibold shadow-md ${
                          urgencyLevel === "overdue"
                            ? "bg-red-600 text-white"
                            : "bg-amber-500 text-white"
                        }`}
                        data-testid={`contractor-urgency-badge-${c.id}`}
                      >
                        {urgencyLevel === "overdue"
                          ? `${overdue} overdue`
                          : `${urgent} urgent`}
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-display font-semibold text-white ${
                          urgencyLevel === "overdue" ? "bg-red-600" : "bg-[#001E44]"
                        }`}>
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-display font-semibold text-base leading-tight text-[#001E44]">{c.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">
                            {c.trade || "General"}
                          </div>
                        </div>
                      </div>
                      {canManageContractors && (
                        <DeleteContractor projectId={id} contractor={c} onDeleted={load} />
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        <span className="font-mono font-semibold text-foreground">{cItems.length}</span> items
                        {overdue > 0 && (
                          <span className="ml-2 font-mono font-semibold text-red-600" data-testid={`contractor-overdue-count-${c.id}`}>
                            · {overdue} overdue
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => setSelectedContractor(c.id)}
                        className={`font-semibold uppercase tracking-wider hover:underline inline-flex items-center gap-1 ${
                          urgencyLevel === "overdue"
                            ? "text-red-600"
                            : "text-[hsl(var(--primary))]"
                        }`}
                        data-testid={`view-contractor-items-${c.id}`}
                      >
                        View items <CaretRight size={11} weight="bold" />
                      </button>
                    </div>
                    {canManageContractors && (
                      <div className="mt-3 pt-3 border-t border-zinc-100">
                        {c.user_id ? (
                          <span className="chip bg-emerald-50 text-emerald-700">
                            <LinkIcon size={11} weight="bold" /> Login linked
                          </span>
                        ) : c.contact_email ? (
                          <InviteButton projectId={id} contractor={c} />
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                            Add contact email to invite
                          </span>
                        )}
                      </div>
                    )}
                    {(user?.role === "admin" || isOwner || c.user_id === user?.id) && (
                      <div className="mt-3 pt-3 border-t border-zinc-100">
                        <ShareSheetButton projectId={id} contractor={c} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Items */}
        <section className="space-y-4" data-testid="items-section">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="label-eyebrow text-[#007BFF]">
                {useContractorDrillIn
                  ? (selectedContractorObj ? "Contractor items" : "Procurement")
                  : "Procurement"}
              </span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">
                {useContractorDrillIn && selectedContractorObj
                  ? selectedContractorObj.name
                  : "Items"}
              </h2>
              {useContractorDrillIn && selectedContractorObj && (
                <button
                  type="button"
                  onClick={() => setSelectedContractor(null)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] font-semibold text-zinc-500 hover:text-[#007BFF] transition-colors"
                  data-testid="back-to-contractors-button"
                >
                  <CaretRight size={11} weight="bold" className="rotate-180" />
                  Back to contractors
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!isStandaloneContractor && !useContractorDrillIn && contractors.length > 0 && (
                <Select
                  value={selectedContractor ?? "all"}
                  onValueChange={setSelectedContractor}
                >
                  <SelectTrigger className="w-[220px] rounded-md" data-testid="contractor-filter">
                    <SelectValue placeholder="Filter by contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {memberLabelPlural}</SelectItem>
                    {contractors.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {useContractorDrillIn && selectedContractorObj && (
                <Select
                  value={selectedContractor}
                  onValueChange={setSelectedContractor}
                >
                  <SelectTrigger className="w-[220px] rounded-md" data-testid="contractor-switcher">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {contractors.length > 0 && canManageItems && (
                <ImportScheduleDialog
                  projectId={id}
                  contractors={contractors}
                  onDone={load}
                  defaultContractorId={
                    isStandaloneContractor && defaultBucket
                      ? defaultBucket.id
                      : null
                  }
                />
              )}
              {contractors.length > 0 && canManageItems
                && (!useContractorDrillIn || !!selectedContractorObj) && (
                <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-sm font-semibold px-5 py-2.5 h-auto shadow-sm hover:shadow-md transition-all" data-testid="new-item-button">
                      <Plus size={14} weight="bold" className="mr-2" /> Add item
                    </Button>
                  </DialogTrigger>
                  <NewItemDialog
                    projectId={id}
                    contractors={contractors}
                    onCreated={() => { setNewItemOpen(false); load(); }}
                    defaultContractorId={
                      useContractorDrillIn
                        ? selectedContractor
                        : (isStandaloneContractor && defaultBucket ? defaultBucket.id : null)
                    }
                    hideContractorPicker={isStandaloneContractor || useContractorDrillIn}
                  />
                </Dialog>
              )}
            </div>
          </div>

          {useContractorDrillIn && !selectedContractorObj ? (
            <div
              className="ctrl-card p-12 text-center border-dashed"
              data-testid="drill-in-prompt"
            >
              <Package size={32} weight="bold" className="mx-auto text-[#007BFF] mb-3" />
              <p className="font-display text-lg font-bold tracking-tight text-[#001E44]">
                Pick a contractor to see their procurement items.
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Click any company card above (or use the &ldquo;View items&rdquo; link)
                to see just their procurement schedule — clearer than one long combined list.
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="ctrl-card p-12 text-center">
              <Package size={32} weight="bold" className="mx-auto text-muted-foreground mb-3" />
              <p className="font-display text-lg font-bold tracking-tight">
                No procurement items yet.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Add items to track their priority, status and ETA.
              </p>
            </div>
          ) : (
            <div className="ctrl-card overflow-hidden">
              {filteredItems.length > 3 && (
                <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/50 flex items-center gap-3">
                  <MagnifyingGlass size={14} weight="bold" className="text-zinc-400 shrink-0" />
                  <input
                    type="search"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder="Search by code, item, supplier or PO#…"
                    className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-400"
                    data-testid="items-search-input"
                  />
                  {itemSearch && (
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500" data-testid="items-search-count">
                      {visibleItems.length} of {filteredItems.length}
                    </span>
                  )}
                  {itemSort.key && (
                    <button
                      type="button"
                      onClick={() => setItemSort({ key: null, dir: "asc" })}
                      className="text-[10px] uppercase tracking-wider font-semibold text-[#007BFF] hover:underline"
                      data-testid="clear-sort"
                    >
                      Clear sort
                    </button>
                  )}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[880px]" data-testid="items-table">
                  <thead>
                    <tr>
                      <th className="w-[100px]">
                        <SortableTh
                          label="Code"
                          sortKey="code"
                          sort={itemSort}
                          onToggle={toggleSort}
                          testid="sort-code"
                        />
                      </th>
                      <th>
                        <SortableTh
                          label="Item"
                          sortKey="name"
                          sort={itemSort}
                          onToggle={toggleSort}
                          testid="sort-name"
                        />
                      </th>
                      <th>Contractor</th>
                      <th>Priority</th>
                      <th className="w-[280px]">Status</th>
                      <th>
                        <SortableTh
                          label="ETA"
                          sortKey="eta"
                          sort={itemSort}
                          onToggle={toggleSort}
                          testid="sort-eta"
                        />
                      </th>
                      <th>Qty</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((i) => (
                      <ItemRow
                        key={i.id}
                        item={i}
                        contractor={contractors.find((c) => c.id === i.contractor_account_id)}
                        canDelete={canManageItems}
                        canEdit={canManageItems}
                        canApprove={canManageContractors}
                        onChanged={load}
                        onOpen={(it) => setOpenItem(it)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
      <ItemDetailSheet
        open={!!openItem}
        item={openItem}
        onClose={() => setOpenItem(null)}
        onUpdated={load}
      />
      <ProjectActivityPanel
        projectId={id}
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
      />
    </AppShell>
  );
}

function SortableTh({ label, sortKey, sort, onToggle, testid }) {
  const active = sort.key === sortKey;
  const Icon = !active ? CaretUpDown : sort.dir === "asc" ? CaretUp : CaretDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(sortKey)}
      className={`inline-flex items-center gap-1 -my-1 py-1 ${
        active ? "text-[#007BFF]" : "text-inherit hover:text-[#001E44]"
      }`}
      data-testid={testid}
    >
      {label}
      <Icon size={10} weight="bold" className={active ? "opacity-100" : "opacity-40"} />
    </button>
  );
}

function ItemRow({ item, contractor, canDelete, canEdit, canApprove, onChanged, onOpen }) {
  const updateField = async (field, value) => {
    try {
      await api.patch(`/items/${item.id}`, { [field]: value });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/items/${item.id}`);
      toast.success("Item deleted");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleApprove = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/items/${item.id}/approve`);
      toast.success("Item approved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    }
  };

  return (
    <tr data-testid={`item-row-${item.id}`}>
      <td>
        <button
          onClick={() => onOpen?.(item)}
          className="text-left"
          data-testid={`open-item-code-${item.id}`}
        >
          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
            item.code
              ? "bg-[#001E44]/5 text-[#001E44] font-semibold"
              : "text-zinc-300"
          }`} title={item.code ? `SKU: ${item.code}` : "No code / SKU set"}>
            {item.code || "—"}
          </span>
        </button>
      </td>
      <td>
        <button
          onClick={() => onOpen?.(item)}
          className="text-left hover:text-[hsl(var(--primary))] transition-colors"
          data-testid={`open-item-${item.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{item.name}</span>
            <OverdueBadge item={item} />
          </div>
          {item.description && (
            <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
          )}
        </button>
      </td>
      <td className="text-xs">{contractor?.name || "—"}</td>
      <td>
        <Select value={item.priority} disabled={!canEdit} onValueChange={(v) => updateField("priority", v)}>
          <SelectTrigger className="w-[110px] h-8 rounded-md border-none p-0 bg-transparent shadow-none focus:ring-0">
            <PriorityChip priority={item.priority} />
          </SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value} data-testid={`set-priority-${item.id}-${p.value}`}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td>
        <Select value={item.status} disabled={!canEdit} onValueChange={(v) => updateField("status", v)}>
          <SelectTrigger className="w-full h-auto rounded-md border-none p-0 bg-transparent shadow-none focus:ring-0">
            <StatusBar status={item.status} compact />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`set-status-${item.id}-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="font-mono text-xs">
        {item.eta ? new Date(item.eta).toLocaleDateString() : "—"}
      </td>
      <td className="font-mono text-xs whitespace-nowrap">
        {item.quantity} {item.unit}
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          {canApprove && item.status === "awaiting_approval" && (
            <button
              onClick={handleApprove}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              data-testid={`approve-item-${item.id}`}
              title="Approve item"
            >
              <Check size={11} weight="bold" /> Approve
            </button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="text-zinc-400 hover:text-destructive transition-colors p-1"
                  data-testid={`delete-item-${item.id}`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                  <AlertDialogDescription>
                    &ldquo;{item.name}&rdquo; will be removed. This can&apos;t be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
}

function DeleteContractor({ projectId, contractor, onDeleted }) {
  const handle = async () => {
    try {
      await api.delete(`/projects/${projectId}/contractors/${contractor.id}`);
      toast.success("Contractor removed");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="text-zinc-400 hover:text-destructive transition-colors p-1" data-testid={`delete-contractor-${contractor.id}`}>
          <Trash size={14} weight="bold" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove contractor?</AlertDialogTitle>
          <AlertDialogDescription>
            &ldquo;{contractor.name}&rdquo; and all of their items in this project will be removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handle} className="bg-destructive hover:bg-destructive/90">
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AddProjectTeamMemberDialog({ projectId, onDone }) {
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ email: "", job_title: "", team_access: "editor" });
  const [busyInvite, setBusyInvite] = useState(false);

  const ACCESS_LABEL = { full: "Full access", editor: "Editor", viewer: "Viewer" };
  const ACCESS_DESCRIPTION = {
    full: "Can create projects, add items, and invite teammates.",
    editor: "Can add/remove items but can't create projects or invite teammates.",
    viewer: "Read-only access to projects and items.",
  };

  const loadEligible = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/eligible-team-members`);
      setEligible(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load teammates");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadEligible();
  }, [loadEligible]);

  const addExisting = async (memberId) => {
    setBusyId(memberId);
    try {
      await api.post(`/projects/${projectId}/members`, { user_id: memberId });
      toast.success("Team member added to project");
      await loadEligible();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setBusyInvite(true);
    try {
      await api.post("/team/contractor-invites", {
        ...form,
        project_ids: [projectId],
      });
      toast.success(`Invite sent to ${form.email}. They'll be added to this project when they accept.`);
      setForm({ email: "", job_title: "", team_access: "editor" });
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyInvite(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="add-project-team-member-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
          Add a team member to this project
        </DialogTitle>
        <p className="text-xs text-zinc-500 mt-1 leading-snug">
          Members you add here only get access to this project — not your other projects.
        </p>
      </DialogHeader>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="existing" data-testid="tab-add-existing">
            From your team
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="tab-invite-new">
            Invite by email
          </TabsTrigger>
        </TabsList>

        <TabsContent value="existing" className="mt-4">
          {loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Loading teammates…</div>
          ) : eligible.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-eligible-teammates">
              Everyone on your team is already on this project — invite someone new using the tab above.
            </div>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="eligible-teammates-list">
              {eligible.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors"
                  data-testid={`eligible-teammate-${m.id}`}
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-lg flex items-center justify-center font-display font-semibold text-sm shrink-0">
                    {m.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#001E44] truncate">{m.name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {m.job_title || "Teammate"} · {ACCESS_LABEL[m.team_access] || m.team_access}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId === m.id}
                    onClick={() => addExisting(m.id)}
                    className="bg-[#007BFF] hover:bg-[#0062cc] text-white rounded-full text-xs font-semibold h-8"
                    data-testid={`add-teammate-${m.id}`}
                  >
                    {busyId === m.id ? "Adding…" : "Add"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="new" className="mt-4">
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Email</Label>
              <Input
                required type="email" autoComplete="off"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="teammate@yourcompany.com"
                data-testid="invite-team-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Job title</Label>
              <Input
                required maxLength={120}
                value={form.job_title}
                onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                placeholder="e.g., Site Supervisor, Foreman, Estimator"
                data-testid="invite-team-job-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Access level</Label>
              <Select
                value={form.team_access}
                onValueChange={(v) => setForm((f) => ({ ...f, team_access: v }))}
              >
                <SelectTrigger data-testid="invite-team-access-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["full", "editor", "viewer"].map((k) => (
                    <SelectItem key={k} value={k}>{ACCESS_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 leading-snug">
                {ACCESS_DESCRIPTION[form.team_access]} They&apos;ll only be added to this project.
              </p>
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="submit" disabled={busyInvite}
                className="bg-[#007BFF] hover:bg-[#0066D6] text-white rounded-full font-semibold px-5"
                data-testid="invite-team-submit"
              >
                {busyInvite ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}

function RemoveProjectMemberButton({ projectId, member, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/members/${member.id}`);
      toast.success(`${member.name} removed from project`);
      setOpen(false);
      onRemoved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded text-zinc-400 hover:bg-red-50 hover:text-red-700 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
          title="Remove from this project"
          data-testid={`remove-project-member-${member.id}`}
        >
          <Trash size={14} weight="bold" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`remove-project-member-dialog-${member.id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {member.name} from this project?</AlertDialogTitle>
          <AlertDialogDescription>
            They&apos;ll lose access to this project&apos;s procurement items. Their access to your other projects is unaffected, and they remain on your company team.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={remove}
            className="bg-destructive hover:bg-destructive/90"
            data-testid={`remove-project-member-confirm-${member.id}`}
          >
            {busy ? "Removing…" : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function NewContractorDialog({ projectId, users, onCreated, memberLabel = "contractor", isStandaloneContractor = false }) {  const [form, setForm] = useState({
    name: "", trade: "", contact_email: "", user_id: "__none__",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.user_id === "__none__") payload.user_id = null;
      await api.post(`/projects/${projectId}/contractors`, payload);
      toast.success(memberLabel === "team member" ? "Team member added" : "Contractor added");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="new-contractor-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight capitalize">Add {memberLabel}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">{isStandaloneContractor ? "Name" : "Company / Name"}</Label>
          <Input
            required value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={isStandaloneContractor ? "e.g. Jane Smith" : "e.g. ACME Electrical"}
            data-testid="contractor-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">{isStandaloneContractor ? "Job title" : "Trade"}</Label>
          <Input
            value={form.trade}
            onChange={(e) => setForm((f) => ({ ...f, trade: e.target.value }))}
            placeholder={isStandaloneContractor ? "e.g. Site Supervisor, Foreman, Estimator" : "e.g. Electrical / Plumbing / HVAC"}
            data-testid="contractor-trade-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Contact email <span className="text-red-600">*</span></Label>
          <Input
            type="email" required value={form.contact_email}
            onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            placeholder={isStandaloneContractor ? "jane@yourcompany.com" : "contact@acme.com"}
            data-testid="contractor-email-input"
          />
          <p className="text-xs text-zinc-500">
            {isStandaloneContractor
              ? "Used for overdue-item alerts and updates about this assignment."
              : "Required — used for activation links and overdue-item alerts."}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">
            {isStandaloneContractor ? "Assign to teammate (optional)" : "Link to contractor account (optional)"}
          </Label>
          <Select value={form.user_id} onValueChange={(v) => setForm((f) => ({ ...f, user_id: v }))}>
            <SelectTrigger className="rounded-md" data-testid="contractor-user-select">
              <SelectValue placeholder={isStandaloneContractor ? "Not assigned" : "No login linked"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                {isStandaloneContractor ? "Not assigned" : "No login linked"}
              </SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Linking lets the contractor log in and manage only their own items.
          </p>
        </div>
        <DialogFooter className="pt-2">
          <Button type="submit" disabled={busy} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 capitalize" data-testid="submit-new-contractor">
            {busy ? "Adding..." : `Add ${memberLabel}`}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function NewItemDialog({ projectId, contractors, onCreated, defaultContractorId = null, hideContractorPicker = false }) {
  const [form, setForm] = useState({
    contractor_account_id: defaultContractorId || contractors[0]?.id || "",
    name: "", description: "", quantity: 1, unit: "pcs",
    priority: "medium", status: "awaiting_approval", eta: "", notes: "",
    location: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contractor_account_id) {
      toast.error(hideContractorPicker ? "Default bucket missing — try reloading" : "Please select a contractor");
      return;
    }
    setBusy(true);
    try {
      const { contractor_account_id, ...body } = form;
      body.quantity = Number(body.quantity);
      if (!body.eta) body.eta = null;
      await api.post(
        `/projects/${projectId}/contractors/${contractor_account_id}/items`,
        body,
      );
      toast.success("Item added");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="new-item-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight">New procurement item</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        {!hideContractorPicker && (
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Contractor</Label>
            <Select
              value={form.contractor_account_id}
              onValueChange={(v) => setForm((f) => ({ ...f, contractor_account_id: v }))}
            >
              <SelectTrigger className="rounded-md" data-testid="item-contractor-select">
                <SelectValue placeholder="Select contractor" />
              </SelectTrigger>
              <SelectContent>
                {contractors.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Item name</Label>
          <Input
            required value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. 12-gauge copper wire, 500ft spool"
            data-testid="item-name-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Quantity</Label>
            <Input
              type="number" min="0" step="0.01" required value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              data-testid="item-quantity-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Unit</Label>
            <Input
              value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              data-testid="item-unit-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="rounded-md" data-testid="item-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="rounded-md" data-testid="item-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Location on site</Label>
          <Input
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="e.g. Level 3 — East wing, Mechanical room"
            data-testid="item-location-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">ETA</Label>
          <DatePicker
            value={form.eta}
            onChange={(v) => setForm((f) => ({ ...f, eta: v }))}
            data-testid="item-eta-input"
            placeholder="Select expected date"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Description / Notes</Label>
          <Textarea
            rows={2} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            data-testid="item-description-input"
          />
        </div>
        <DialogFooter className="pt-2">
          <Button type="submit" disabled={busy} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90" data-testid="submit-new-item">
            {busy ? "Adding..." : "Add item"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


function ProjectShareMenu({ projectId, projectName, items, contractors, canShare }) {
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const ensureShareUrl = useCallback(async () => {
    if (shareUrl) return shareUrl;
    setLoadingUrl(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/share-link`);
      setShareUrl(data.share_url);
      return data.share_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create share link");
      return "";
    } finally {
      setLoadingUrl(false);
    }
  }, [projectId, shareUrl]);

  // Pre-fetch share URL on open
  useEffect(() => {
    if (open && !shareUrl && canShare) {
      ensureShareUrl();
    }
  }, [open, canShare, shareUrl, ensureShareUrl]);

  const copyLink = async () => {
    const url = await ensureShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — open and paste manually");
    }
  };

  const downloadCSV = () => {
    const contractorById = (contractors || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
    const headers = ["Item", "Contractor", "Description", "Priority", "Status", "Quantity", "Unit", "ETA", "Location", "Notes"];
    const rows = (items || []).map((i) => [
      i.name || "",
      contractorById[i.contractor_account_id]?.name || "",
      i.description || "",
      i.priority || "",
      i.status || "",
      i.quantity ?? "",
      i.unit || "",
      i.eta || "",
      i.location || "",
      i.notes || "",
    ]);
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${projectName.replace(/[^\w\d-]+/g, "_")}-schedule.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSV downloaded");
  };

  const printPDF = async () => {
    const url = await ensureShareUrl();
    if (!url) return;
    // Open the public read-only view in a new tab — user can Cmd+P to PDF.
    window.open(url + "?print=1", "_blank", "noopener");
  };

  if (!canShare) return null;

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 hover:bg-white text-[#001E44] text-xs sm:text-sm font-semibold shadow-md backdrop-blur-sm transition-all"
            data-testid="project-share-button"
          >
            <ShareNetwork size={14} weight="bold" /> Share
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="project-share-menu">
          <div className="px-3 py-2.5">
            <p className="label-eyebrow text-zinc-400">Share procurement schedule</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">Read-only · auto-refreshes · no login required</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); copyLink(); }} data-testid="share-copy-link">
            <LinkIcon size={14} weight="bold" className="mr-2 text-[#007BFF]" />
            <div className="flex-1">
              <div className="font-medium">Copy public link</div>
              <div className="text-[11px] text-zinc-500">{loadingUrl ? "Creating link…" : "Anyone with the link can view"}</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setEmailDialogOpen(true); }} data-testid="share-email">
            <EnvelopeSimple size={14} weight="bold" className="mr-2 text-[#007BFF]" />
            <div className="flex-1">
              <div className="font-medium">Email the schedule</div>
              <div className="text-[11px] text-zinc-500">Send the link directly to people outside the platform</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); downloadCSV(); }} data-testid="share-csv">
            <FileCsv size={14} weight="bold" className="mr-2 text-[#007BFF]" />
            <div className="flex-1">
              <div className="font-medium">Download CSV</div>
              <div className="text-[11px] text-zinc-500">Open in Excel, Sheets or Numbers</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); printPDF(); }} data-testid="share-pdf">
            <FilePdf size={14} weight="bold" className="mr-2 text-[#007BFF]" />
            <div className="flex-1">
              <div className="font-medium">Print / Save as PDF</div>
              <div className="text-[11px] text-zinc-500">Opens the print-friendly schedule</div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EmailScheduleDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        projectId={projectId}
        projectName={projectName}
      />
    </>
  );
}

function EmailScheduleDialog({ open, onOpenChange, projectId, projectName }) {
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const list = recipients
      .split(/[\s,;]+/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/share-link/email`, {
        recipients: list,
        message,
      });
      if (data.failed && data.failed.length > 0) {
        toast.warning(
          `Sent to ${data.sent.length}/${list.length}. Failed: ${data.failed.join(", ")}`,
        );
      } else {
        toast.success(`Schedule emailed to ${data.sent.length} recipient${data.sent.length === 1 ? "" : "s"}`);
      }
      setRecipients("");
      setMessage("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="email-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
            Email the schedule
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-zinc-600">
            We&apos;ll send the live, read-only view of <span className="font-semibold">{projectName}</span> to whoever you list below. They don&apos;t need an account.
          </p>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Recipients</Label>
            <Input
              required
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="alice@firmA.com, bob@firmB.com"
              data-testid="email-recipients-input"
            />
            <p className="text-[11px] text-zinc-500 leading-snug">
              Separate multiple emails with commas, spaces, or new lines.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Personal note (optional)</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hey team, here's the live schedule for review…"
              data-testid="email-message-input"
            />
          </div>
          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit" disabled={busy}
              className="bg-[#007BFF] hover:bg-[#0066D6] text-white rounded-full font-semibold px-5"
              data-testid="email-schedule-submit"
            >
              <EnvelopeSimple size={12} weight="bold" className="mr-1.5" />
              {busy ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
